<template>
  <div>
    <el-card v-loading="loading">
      <template #header>
        <div class="title1">
          <span>出库数量占比</span>
          <el-col :span="12">
            <el-date-picker
              v-model="datePicker"
              type="daterange"
              placeholder="请选择"
              style="width: 100%"
              @change="datePickerChange"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              :clearable="false"
            />
          </el-col>
        </div>
      </template>
      <div ref="chart" class="chart"></div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import * as echarts from "echarts/core";
import {
  TooltipComponent,
  LegendComponent,
  GraphicComponent,
} from "echarts/components";
import { PieChart } from "echarts/charts";
import { LabelLayout } from "echarts/features";
import { CanvasRenderer } from "echarts/renderers";
import {
  getCurrentInstance,
  reactive,
  watchEffect,
  onUnmounted,
  nextTick,
  onMounted,
} from "vue";
import { outQuantityPieChart } from "@/api/statistics";
import { time } from "echarts/core";
echarts.use([
  TooltipComponent,
  LegendComponent,
  PieChart,
  CanvasRenderer,
  LabelLayout,
  GraphicComponent,
]);

const option = reactive({
  title: {
    text: "这是",
    left: "center",
    top: "center",
    textStyle: {
      color: "#000",
      fontSize: 24,
      fontWeight: "bold",
    },
  },
  tooltip: {
    trigger: "item",
  },
  legend: {
    top: "center",
    right: "20",
    icon: "circle",
    orient: "vertical",
    formatter: (name) => {
      return (
        name +
        "     " +
        arr.value.find((item) => item.name === name).rate +
        "%" +
        "     " +
        arr.value.find((item) => item.name === name).quantity
      );
    },

    itemWidth: 10,
    itemHeight: 10,
  },
  series: [
    {
      type: "pie",
      radius: ["40%", "70%"],
      avoidLabelOverlap: false,
      label: {
        show: true,
        position: "center",
        color: "#4c4a4a",
        rich: {
          total: {
            fontSize: 20,
            fontWeight: 700,
            fontFamily: "微软雅黑",
            color: "#4a8aff",
          },
          active: {
            fontFamily: "微软雅黑",
            fontSize: 12,
            color: "#6c7a89",
            lineHeight: 30,
          },
        },
        emphasis: {
          show: true,
        },
      },
      labelLine: {
        show: false,
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 40,
          fontWeight: "bold",
        },
      },

      data: [],
    },
  ],
  graphic: {
    elements: [
      {
        type: "text",
        left: "center",
        top: "middle",
        silent: true,
        invisible: false,
        style: {
          fill: "black",
          fontWeight: "bold",
          text: "暂无数据",
          fontSize: "26px",
        },
        z: 200,
      },
    ],
  },
});

const charts = {
  chart: null,
};

const arr = ref([]);

const total = ref(null);

const loading = ref(false);

const { proxy } = getCurrentInstance();

// 日期范围选择器
const datePicker = ref("");

// 时间范围选择器变化
const datePickerChange = (val) => {
  searchData.startTime = val?.[0] + " 00:00:00" ?? null;
  searchData.endTime = val?.[1] + " 23:59:59" ?? null;
  getChart();
};

// 搜索表单
const searchData = reactive({
  startTime: null,
  endTime: null,
});

const initChart = () => {
  if (charts.chart) charts.chart.dispose();
  const chartDom = proxy.$refs.chart;
  charts.chart = echarts.init(chartDom);
  charts.chart.setOption(option);

  const resizeHandler = () => {
    charts.chart.resize();
  };

  window.addEventListener("resize", resizeHandler);

  watchEffect(() => {
    onUnmounted(() => {
      window.removeEventListener("resize", resizeHandler);
      charts.chart.dispose();
    });
  });
};

const getChart = () => {
  loading.value = true;
  outQuantityPieChart({ ...searchData })
    .then((res) => {
      arr.value = res.data || [];
      option.series[0].data = arr.value.map((item) => {
        return { value: item.quantity, name: item.name };
      });
      let num = 0;
      arr.value.forEach((item) => {
        num = num + item.quantity;
      });
      total.value = num;
      option.series[0].label.formatter = "{total|" + total.value + "}";
      option.graphic.elements[0].invisible =
        arr.value.length == 0 ? false : true;
    })
    .finally(() => {
      charts.chart.setOption(option);
      loading.value = false;
    });
};

const getLastWeekRange = () => {
  // 获取当前日期
  let now = new Date();

  // // 获取当前日期的时间戳
  // let currentTimestamp = now.getTime();

  // 计算一个月前的时间戳
  let oneMonthAgo = new Date(now);
  oneMonthAgo.setMonth(now.getMonth() - 1);

  // 格式化日期
  function formatDate(date) {
    let year = date.getFullYear();
    let month = (date.getMonth() + 1).toString().padStart(2, "0");
    let day = date.getDate().toString().padStart(2, "0");
    // let hours = date.getHours().toString().padStart(2, "0");
    // let minutes = date.getMinutes().toString().padStart(2, "0");
    // let seconds = date.getSeconds().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // 获取一月前和当前时间的格式化字符串
  let oneMonthAgoFormatted = formatDate(oneMonthAgo);
  let nowFormatted = formatDate(now);

  datePicker.value = [oneMonthAgoFormatted, nowFormatted];
  searchData.startTime = datePicker.value?.[0] + " 00:00:00" ?? null;
  searchData.endTime = datePicker.value?.[1] + " 23:59:59" ?? null;
};

onMounted(() => {
  getLastWeekRange();
  getChart();
  nextTick(() => {
    initChart();
  });
});
</script>

<style scoped>
.chart {
  height: 500px;
  width: 100%;
}
.title1 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 20px;
}
</style>
